import { useEffect, useRef } from "react";
import { Card, Col, Form, Placeholder, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useParams, useBlocker } from "react-router-dom";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import { useTabelaPreco } from "hooks/useTabelaPreco";
import { TipoCalculoTabelaPreco } from "services/tabelaPrecoService";
import BotaoSalvarTabelaPreco from "components/tabelaPreco/BotaoSalvarTabelaPreco";
import RegrasEspeciaisCard from "components/tabelaPreco/RegrasEspeciaisCard";
import EmpresasCard from "components/tabelaPreco/EmpresasCard";

// ─── Tipos exportados para os componentes filhos ────────────────────────────────

export interface TabelaPrecoFormValues {
  nome: string;
  ativa: boolean;
  tipoCalculo: TipoCalculoTabelaPreco | "";
  percentual: number;
  empresasIds: string[];
  regrasEspeciais: {
    produtoId: string;
    produtoCodigo?: number;
    produtoDescricao?: string;
    produtoCodigoBarras?: string;
    tipoCalculo: TipoCalculoTabelaPreco;
    percentual: number;
  }[];
}

// ─── Labels dos tipos de cálculo ───────────────────────────────────────────────

export const tipoCalculoLabels: Record<TipoCalculoTabelaPreco, string> = {
  [TipoCalculoTabelaPreco.AcrescimoSobreVenda]: "Acréscimo sobre o preço de venda",
  [TipoCalculoTabelaPreco.DescontoSobreVenda]: "Desconto sobre o preço de venda",
  [TipoCalculoTabelaPreco.MarkupSobreCusto]: "Markup sobre o preço de custo",
  [TipoCalculoTabelaPreco.MargemLucro]: "Margem de lucro",
  [TipoCalculoTabelaPreco.ValorFixo]: "Valor fixo",
};

// ─── Breadcrumb ────────────────────────────────────────────────────────────────

const buildBreadcrumb = (isEdit: boolean) => [
  { label: "Cadastros" },
  { label: "Tabela de Preços", url: "/cadastros/tabela-preco" },
  { label: isEdit ? "Editar" : "Nova", active: true },
];

// ─── Formulário ────────────────────────────────────────────────────────────────

const TabelaPrecoForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data: detalhe, isLoading } = useTabelaPreco(id ?? "", isEdit);

  const form = useForm<TabelaPrecoFormValues>({
    defaultValues: {
      nome: "",
      ativa: true,
      tipoCalculo: TipoCalculoTabelaPreco.AcrescimoSobreVenda,
      percentual: 0,
      empresasIds: [],
      regrasEspeciais: [],
    },
  });

  const { register, watch, reset, formState: { errors, isDirty } } = form;

  // Ref para liberar a navegação logo após salvar (o reset não limpa isDirty
  // a tempo para o closure do blocker — então sinalizamos via ref).
  const bypassBlocker = useRef(false);

  // Bloqueia a navegação se houver alterações não salvas (padrão do projeto)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !bypassBlocker.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // Carrega os dados do detalhe no formulário ao editar
  useEffect(() => {
    if (detalhe) {
      reset({
        nome: detalhe.nome,
        ativa: detalhe.ativa,
        tipoCalculo: detalhe.tipoCalculo,
        percentual: detalhe.percentual,
        empresasIds: detalhe.empresasIds ?? [],
        regrasEspeciais: (detalhe.regrasEspeciais ?? []).map((r) => ({
          produtoId: r.produtoId,
          produtoCodigo: r.produtoCodigo,
          produtoDescricao: r.produtoDescricao,
          produtoCodigoBarras: r.produtoCodigoBarras,
          tipoCalculo: r.tipoCalculo,
          percentual: r.percentual,
        })),
      });
    }
  }, [detalhe, reset]);

  const tipoCalculo = watch("tipoCalculo");
  const isValorFixo = tipoCalculo === TipoCalculoTabelaPreco.ValorFixo;

  if (isEdit && isLoading) {
    return (
      <div>
        <PageBreadcrumb items={buildBreadcrumb(true)} />
        <div className="mb-9">
          <div className="mb-4">
            <Placeholder animation="wave" as="h2" className="mb-1">
              <Placeholder xs={4} />
            </Placeholder>
            <Placeholder animation="wave" as="p" className="mb-0">
              <Placeholder xs={6} />
            </Placeholder>
          </div>

          <Card className="mb-4">
            <Card.Header className="border-bottom d-flex align-items-center justify-content-between">
              <Placeholder animation="wave" as="h5" className="mb-0">
                <Placeholder xs={3} style={{ width: 140 }} />
              </Placeholder>
              <Placeholder.Button variant="secondary" xs={1} style={{ width: 60 }} />
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <Placeholder animation="wave" as="div" className="mb-1"><Placeholder xs={2} /></Placeholder>
                  <Placeholder animation="wave" as="div"><Placeholder xs={12} style={{ height: 38 }} /></Placeholder>
                </Col>
                <Col xs={12} md={6}>
                  <Placeholder animation="wave" as="div" className="mb-1"><Placeholder xs={3} /></Placeholder>
                  <Placeholder animation="wave" as="div"><Placeholder xs={12} style={{ height: 38 }} /></Placeholder>
                </Col>
                <Col xs={12} md={6}>
                  <Placeholder animation="wave" as="div" className="mb-1"><Placeholder xs={3} /></Placeholder>
                  <Placeholder animation="wave" as="div"><Placeholder xs={12} style={{ height: 38 }} /></Placeholder>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={buildBreadcrumb(isEdit)} />

      <FormProvider {...form}>
        <UnsavedChangesModal
          show={blocker.state === "blocked"}
          onConfirm={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />

        <div className="mb-9">
          <div className="mb-4">
            <h2 className="mb-1">
              {isEdit ? "Editar Tabela de Preços" : "Nova Tabela de Preços"}
            </h2>
            <p className="text-body-secondary mb-0">
              Preencha as informações da tabela de preços
            </p>
          </div>

          {/* Card principal */}
          <Card className="mb-4">
            <Card.Header className="border-bottom d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                Tabela de Preços
                {isEdit && detalhe && (
                  <span className="badge bg-primary-subtle text-primary fs-10 ms-2 align-middle">
                    #{detalhe.codigo}
                  </span>
                )}
              </h5>
              <Form.Check
                type="switch"
                id="ativa"
                label="Ativa"
                className="mb-0"
                {...register("ativa")}
              />
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                {/* Nome */}
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>
                      Nome da tabela <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      {...register("nome", { required: "O nome é obrigatório" })}
                      placeholder="Ex: Tabela Convênio, Tabela Especial..."
                      isInvalid={!!errors.nome}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.nome?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Tipo de cálculo */}
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>
                      Tipo de cálculo <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      {...register("tipoCalculo", {
                        required: "Selecione um tipo de cálculo",
                        valueAsNumber: true,
                      })}
                      isInvalid={!!errors.tipoCalculo}
                    >
                      {(
                        Object.entries(tipoCalculoLabels) as [string, string][]
                      ).map(([value, label]) => (
                        <option key={value} value={Number(value)}>
                          {label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.tipoCalculo?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Percentual — oculto quando ValorFixo */}
                {!isValorFixo && (
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Percentual (%) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        {...register("percentual", {
                          required: "O percentual é obrigatório",
                          min: { value: 0, message: "O percentual deve ser positivo" },
                          valueAsNumber: true,
                        })}
                        onFocus={(e) => e.currentTarget.select()}
                        isInvalid={!!errors.percentual}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.percentual?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}

              </Row>
            </Card.Body>
          </Card>

          {/* Empresas associadas */}
          <EmpresasCard />

          {/* Card de regras especiais */}
          <RegrasEspeciaisCard />

          {/* Rodapé com botão salvar */}
          <div className="d-flex justify-content-end mt-4">
            <BotaoSalvarTabelaPreco id={id} bypassBlockerRef={bypassBlocker} />
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default TabelaPrecoForm;
